import clsx from "clsx";
import type React from "react";
import styles from "./CodeBlock.module.scss";

export interface CodeBlockProps extends React.HTMLAttributes<HTMLPreElement> {
  children: React.ReactNode;
  density?: "compact" | "normal";
  language?: "json" | "plaintext";
  wrap?: boolean;
}

const JSON_TOKEN_PATTERN =
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

const highlightJson = (value: string) => {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  for (const match of value.matchAll(JSON_TOKEN_PATTERN)) {
    const index = match.index ?? 0;
    if (index > cursor) nodes.push(value.slice(cursor, index));

    const token = match[0];
    const tokenClass = match[1]
      ? match[2]
        ? styles.key
        : styles.string
      : match[3]
        ? styles.constant
        : styles.number;

    nodes.push(
      <span key={`${index}-${token}`} className={tokenClass}>
        {token}
      </span>,
    );
    cursor = index + token.length;
  }

  if (cursor < value.length) nodes.push(value.slice(cursor));
  return nodes;
};

const CodeBlock = (props: CodeBlockProps) => {
  const {
    children,
    className,
    density = "normal",
    language = "plaintext",
    wrap = false,
    ...otherProps
  } = props;
  const content =
    language === "json" && typeof children === "string" ? highlightJson(children) : children;

  return (
    <pre
      className={clsx(styles.root, className)}
      data-density={density}
      data-wrap={wrap ? "true" : undefined}
      {...otherProps}
    >
      <code>{content}</code>
    </pre>
  );
};

export default CodeBlock;
